import React, { useMemo } from 'react'

import PLUS_ICON from '@public/assets/action/icon_plus.svg'
import { cn } from '@utils/mergeClassNames'

import type { FileTypeInput, UserFileType } from '../types'
import {
  getFileTypeIcon,
  getFileTypeKey,
  getFileTypeName,
  normalizeUserFileTypes,
} from '../utils/fileTypeUtils'

interface FileTypeSelectorProps {
  fileTypes: FileTypeInput[]
  selectedFileTypes: FileTypeInput[]
  disabled?: boolean
  onChange: (fileTypes: FileTypeInput[]) => void
}

type FileTypeChipMode = 'selected' | 'available'

export function FileTypeSelector({
  fileTypes,
  selectedFileTypes: selectedFileTypeInputs,
  disabled = false,
  onChange,
}: FileTypeSelectorProps) {
  const availableFileTypes = useMemo(
    () => fileTypes.filter(fileType => getFileTypeIcon(fileType)),
    [fileTypes]
  )
  const selectedFileTypes = useMemo(
    () => normalizeUserFileTypes(selectedFileTypeInputs),
    [selectedFileTypeInputs]
  )
  const selectedFileTypeNames = useMemo(
    () => new Set(selectedFileTypes.map(fileType => fileType.name)),
    [selectedFileTypes]
  )
  const notTrackingFileTypes = useMemo(
    () =>
      availableFileTypes.filter(
        fileType => !selectedFileTypeNames.has(getFileTypeName(fileType))
      ),
    [availableFileTypes, selectedFileTypeNames]
  )

  const handleAddFileType = (fileType: FileTypeInput) => {
    const name = getFileTypeName(fileType)
    if (
      !name ||
      selectedFileTypeNames.has(name) ||
      !getFileTypeIcon(fileType)
    ) {
      return
    }
    onChange([...selectedFileTypes, fileType])
  }

  const handleRemoveFileType = (fileType: FileTypeInput) => {
    const name = getFileTypeName(fileType)
    onChange(selectedFileTypes.filter(item => getFileTypeName(item) !== name))
  }

  const renderFileTypeChip = (
    fileType: FileTypeInput,
    mode: FileTypeChipMode
  ) => {
    const name = getFileTypeName(fileType)
    const icon = getFileTypeIcon(fileType)
    if (!name || !icon) return null

    const isSelected = mode === 'selected'
    const baseClass =
      'inline-flex min-h-8 items-center gap-1 rounded-full border px-2.5 text-sm font-normal leading-5 text-[#333]'

    if (isSelected) {
      return (
        <div
          key={getFileTypeKey(fileType)}
          className={cn(
            baseClass,
            'cursor-default border-transparent bg-[#ebebeb]'
          )}
          title={name}
        >
          <img src={icon} alt="" className="h-6 w-6 shrink-0" />
          <span className="whitespace-nowrap">{name}</span>
          <button
            type="button"
            className="inline-flex h-4 w-4 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-base font-semibold leading-4 text-[#666] opacity-80 hover:text-black hover:opacity-100 disabled:cursor-not-allowed"
            onClick={() => handleRemoveFileType(fileType)}
            title={`Remove ${name}`}
            disabled={disabled}
          >
            ×
          </button>
        </div>
      )
    }

    return (
      <button
        type="button"
        key={getFileTypeKey(fileType)}
        className={cn(
          baseClass,
          'group cursor-pointer border-[#d9d9d9] bg-white hover:bg-[rgba(74,68,89,0.08)] disabled:cursor-not-allowed'
        )}
        onClick={() => handleAddFileType(fileType)}
        title={`Add ${name}`}
        disabled={disabled}
      >
        <img src={icon} alt="" className="h-6 w-6 shrink-0" />
        <span className="whitespace-nowrap">{name}</span>
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[#cfcfcf] bg-transparent p-0 opacity-80">
          <img
            src={PLUS_ICON}
            alt=""
            className="block h-4 w-4 brightness-200 group-hover:brightness-0"
          />
        </span>
      </button>
    )
  }

  return (
    <div className="flex flex-col gap-5 rounded-lg bg-white p-5 shadow-[0_4px_18px_0_rgba(119,124,139,0.20)] max-[600px]:p-4">
      <div className="flex w-full flex-col gap-1">
        <div className="flex items-center gap-0.5 text-sm font-normal leading-5 text-[#999] max-[600px]:flex-wrap max-[600px]:items-start">
          <span className="font-medium text-[#ff444b]">*</span>
          <span>File types</span>
          <span>(At least one file type must be selected)</span>
        </div>
        <div className="relative flex min-h-[84px] items-center gap-2.5 rounded-md border border-[#d9d9d9] bg-white px-2.5 py-2">
          <div className="flex min-w-0 flex-[1_1_auto] flex-wrap items-start gap-x-1 gap-y-2">
            {selectedFileTypes.map((fileType: UserFileType) =>
              renderFileTypeChip(fileType, 'selected')
            )}
          </div>
          {selectedFileTypes.length > 0 && (
            <button
              type="button"
              className="flex h-4 w-6 shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-lg leading-4 text-[#666] opacity-80 hover:rounded hover:bg-[rgba(74,68,89,0.08)] hover:text-black hover:opacity-100 disabled:cursor-not-allowed"
              onClick={() => onChange([])}
              title="Remove all file types"
              disabled={disabled}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {notTrackingFileTypes.length > 0 && (
        <div className="flex w-full flex-col gap-1">
          <div className="flex min-h-7 items-center justify-between text-sm font-normal leading-5 text-[#999] max-[600px]:items-start">
            <span>Currently Not Tracking</span>
            <button
              type="button"
              className="inline-flex cursor-pointer items-center gap-1 rounded-full border-0 bg-transparent px-2.5 py-1 text-sm font-normal leading-5 text-[#333] hover:bg-[rgba(74,68,89,0.08)] disabled:cursor-not-allowed disabled:text-[#999]"
              onClick={() =>
                onChange([...selectedFileTypes, ...notTrackingFileTypes])
              }
              disabled={disabled}
            >
              Add all
              <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[#cfcfcf] bg-transparent opacity-80">
                <img
                  src={PLUS_ICON}
                  alt=""
                  className="block h-4 w-4 brightness-200"
                />
              </span>
            </button>
          </div>
          <div className="flex min-h-[84px] items-center rounded-md bg-white px-2.5 py-2">
            <div className="flex min-w-0 flex-[1_1_auto] flex-wrap items-start gap-x-1 gap-y-2">
              {notTrackingFileTypes.map(fileType =>
                renderFileTypeChip(fileType, 'available')
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
